import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Alert, CircularProgress, LinearProgress, Stack, IconButton,
} from '@mui/material';
import { ArrowBack, AutoAwesome, Send, Chat } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { farmOperationService, projectService, aiAdvisorService, seasonService } from '@smartgrow/shared';
import { CurrencyInput } from '../../components/CurrencyInput';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { LocationPicker } from '../../components/LocationPicker';
import type { CreateFarmOperationRequest, OperationType, FarmSeason, YieldUnit, PriceSuggestionResponse, YieldEstimateResponse, EstimateChatMessage } from '@smartgrow/shared';

const YIELD_UNITS: { value: YieldUnit; label: string }[] = [
  { value: 'KG', label: 'Kilograms (kg)' },
  { value: 'BAG_50KG', label: 'Bags (50kg)' },
  { value: 'BAG_100KG', label: 'Bags (100kg)' },
  { value: 'TON', label: 'Tonnes' },
  { value: 'UNIT', label: 'Units (piece count)' },
];

const OPERATION_TYPES: { value: OperationType; label: string; seasonal: boolean }[] = [
  { value: 'SEASONAL_CROP', label: 'Seasonal Crop (corn, cassava, rice, yam)', seasonal: true },
  { value: 'PERENNIAL_CROP', label: 'Perennial Crop (cashew, mango, oil palm, cocoa)', seasonal: false },
  { value: 'LIVESTOCK', label: 'Livestock (fish, cattle, poultry, goat)', seasonal: false },
  { value: 'GENERAL', label: 'General Farm Operation', seasonal: false },
];

const SEASONS: { value: FarmSeason; label: string }[] = [
  { value: 'RAINY', label: 'Rainy Season' },
  { value: 'DRY', label: 'Dry Season' },
];

export const EditFarmOperationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const opId = id!;
  const [error, setError] = useState('');
  const [form, setForm] = useState<CreateFarmOperationRequest>({ projectId: '', name: '', operationType: 'SEASONAL_CROP' });

  const { data: op, isLoading } = useQuery({
    queryKey: ['farm-operation', opId],
    queryFn: () => farmOperationService.getById(opId),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: seasons = [] } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonService.getAll(),
  });

  useEffect(() => {
    if (op) {
      setForm({
        projectId: op.projectId,
        name: op.name ?? '',
        operationType: op.operationType,
        season: op.season,
        seasonInstanceId: op.seasonInstanceId,
        landAreaAcres: op.landAreaAcres,
        latitude: op.latitude,
        longitude: op.longitude,
        cropVariety: op.cropVariety,
        animalType: op.animalType,
        animalQuantity: op.animalQuantity,
        expectedYieldKg: op.expectedYieldKg,
        expectedRevenue: op.expectedRevenue,
        yieldUnit: op.yieldUnit,
        expectedYieldQuantity: op.expectedYieldQuantity,
        expectedPricePerUnit: op.expectedPricePerUnit,
        expectedHarvestingCost: op.expectedHarvestingCost,
        expectedBaggingCost: op.expectedBaggingCost,
        expectedTransportationCost: op.expectedTransportationCost,
        expectedFinalCost: op.expectedFinalCost,
        expectedNetRevenue: op.expectedNetRevenue,
        notes: op.notes,
      });
    }
  }, [op]);

  const projects = projectsData?.content ?? [];
  const selectedType = OPERATION_TYPES.find((t) => t.value === form.operationType);
  const isCrop = form.operationType === 'SEASONAL_CROP' || form.operationType === 'PERENNIAL_CROP';
  const isLivestock = form.operationType === 'LIVESTOCK';
  const isSeasonal = selectedType?.seasonal ?? false;

  const set = (field: keyof CreateFarmOperationRequest, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setYieldProjection = (field: 'expectedYieldQuantity' | 'expectedPricePerUnit', value: number | undefined) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      const quantity = field === 'expectedYieldQuantity' ? value : prev.expectedYieldQuantity;
      const price = field === 'expectedPricePerUnit' ? value : prev.expectedPricePerUnit;
      next.expectedRevenue = quantity && price ? quantity * price : undefined;
      return next;
    });
  };

  const [priceSuggestion, setPriceSuggestion] = useState<PriceSuggestionResponse | null>(null);
  const [priceSuggestionError, setPriceSuggestionError] = useState('');
  const suggestPriceMutation = useMutation({
    mutationFn: () => aiAdvisorService.suggestPrice({
      cropVariety: form.cropVariety || '',
      unitLabel: YIELD_UNITS.find((u) => u.value === form.yieldUnit)?.label,
    }),
    onSuccess: (res) => { setPriceSuggestion(res); setPriceSuggestionError(''); },
    onError: () => { setPriceSuggestion(null); setPriceSuggestionError('Failed to get a price suggestion. Try again.'); },
  });

  const [yieldEstimate, setYieldEstimate] = useState<YieldEstimateResponse | null>(null);
  const [yieldEstimateError, setYieldEstimateError] = useState('');
  const estimateYieldMutation = useMutation({
    mutationFn: () => aiAdvisorService.estimateYield(opId),
    onSuccess: (res) => { setYieldEstimate(res); setYieldEstimateError(''); },
    onError: () => { setYieldEstimate(null); setYieldEstimateError('Failed to get a yield estimate. Try again.'); },
  });

  const [chatMessages, setChatMessages] = useState<EstimateChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatError, setChatError] = useState('');
  const [chatUpdatedEstimate, setChatUpdatedEstimate] = useState<YieldEstimateResponse | null>(null);
  const chatMutation = useMutation({
    mutationFn: (messages: EstimateChatMessage[]) => aiAdvisorService.chatAboutEstimate(opId, { messages }),
    onSuccess: (res) => {
      setChatMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
      setChatUpdatedEstimate(res.updatedEstimate ?? null);
      setChatError('');
    },
    onError: () => setChatError('Failed to get a reply. Try again.'),
  });

  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text || chatMutation.isPending) return;
    const next = [...chatMessages, { role: 'user' as const, content: text }];
    setChatMessages(next);
    setChatInput('');
    chatMutation.mutate(next);
  };

  const applyPriceSuggestion = () => {
    if (priceSuggestion?.pricePerUnit != null) {
      setYieldProjection('expectedPricePerUnit', priceSuggestion.pricePerUnit);
    }
  };

  const applyEstimate = (estimate: YieldEstimateResponse) => {
    setForm((prev) => {
      const next = { ...prev };
      if (estimate.yieldQuantity != null) next.expectedYieldQuantity = estimate.yieldQuantity;
      if (estimate.yieldUnit != null) next.yieldUnit = estimate.yieldUnit;
      if (estimate.pricePerUnit != null) next.expectedPricePerUnit = estimate.pricePerUnit;
      next.expectedHarvestingCost = estimate.harvestingCost;
      next.expectedBaggingCost = estimate.baggingCost;
      next.expectedTransportationCost = estimate.transportationCost;
      next.expectedFinalCost = estimate.finalCost;
      const quantity = next.expectedYieldQuantity;
      const price = next.expectedPricePerUnit;
      next.expectedRevenue = quantity && price ? quantity * price : undefined;
      next.expectedNetRevenue = next.expectedRevenue != null && next.expectedFinalCost != null
        ? next.expectedRevenue - next.expectedFinalCost
        : undefined;
      return next;
    });
  };

  const applyYieldEstimate = () => {
    if (!yieldEstimate) return;
    applyEstimate(yieldEstimate);
  };

  const applyChatUpdatedEstimate = () => {
    if (!chatUpdatedEstimate) return;
    applyEstimate(chatUpdatedEstimate);
    setChatUpdatedEstimate(null);
  };

  const mutation = useMutation({
    mutationFn: (data: CreateFarmOperationRequest) => farmOperationService.update(opId, data),
    onSuccess: () => navigate(`/dashboard/farm/${opId}`),
    onError: () => setError('Failed to update operation.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const payload = { ...form };
    if (!isSeasonal) delete payload.season;
    if (!isCrop) {
      delete payload.cropVariety;
      delete payload.landAreaAcres;
      delete payload.expectedYieldKg;
      delete payload.yieldUnit;
      delete payload.expectedYieldQuantity;
      delete payload.expectedPricePerUnit;
      delete payload.expectedHarvestingCost;
      delete payload.expectedBaggingCost;
      delete payload.expectedTransportationCost;
      delete payload.expectedFinalCost;
      delete payload.expectedNetRevenue;
    }
    if (!isLivestock) { delete payload.animalType; delete payload.animalQuantity; }
    mutation.mutate(payload);
  };

  if (isLoading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Back</Button>
        <Typography variant="h4" fontWeight="bold">Edit Farm Operation</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Operation Details</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth required label="Linked Project" value={form.projectId || ''}
                onChange={(e) => set('projectId', e.target.value)}>
                {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Operation Name" value={form.name ?? ''}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. 2025 Rainy Season Corn, Fish Pond Batch 1" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth required label="Operation Type" value={form.operationType}
                onChange={(e) => { const t = e.target.value as OperationType; setForm((prev) => ({ ...prev, operationType: t, season: undefined })); }}>
                {OPERATION_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            {isSeasonal && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="Season Type" value={form.season ?? ''}
                    onChange={(e) => set('season', e.target.value as FarmSeason)}>
                    {SEASONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="Season (year-specific, optional)" value={form.seasonInstanceId ?? ''}
                    onChange={(e) => set('seasonInstanceId', e.target.value || undefined)}
                    helperText="Enables season-over-season comparison, e.g. 2026 vs 2027">
                    <MenuItem value="">— None —</MenuItem>
                    {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Latitude (optional)" value={form.latitude ?? ''}
                onChange={(e) => set('latitude', e.target.value === '' ? undefined : Number.parseFloat(e.target.value))}
                inputProps={{ step: 'any', min: -90, max: 90 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Longitude (optional)" value={form.longitude ?? ''}
                onChange={(e) => set('longitude', e.target.value === '' ? undefined : Number.parseFloat(e.target.value))}
                inputProps={{ step: 'any', min: -180, max: 180 }} />
            </Grid>
            <Grid item xs={12}>
              <LocationPicker
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={(lat, lng) => setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Notes" value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)} />
            </Grid>
          </Grid>
        </Paper>

        {isCrop && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Crop Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Crop Variety" value={form.cropVariety ?? ''}
                  onChange={(e) => set('cropVariety', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Land Area (acres)" value={form.landAreaAcres ?? ''}
                  onChange={(e) => set('landAreaAcres', Number.parseFloat(e.target.value) || undefined)}
                  inputProps={{ min: 0, step: 0.01 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Expected Yield Quantity" value={form.expectedYieldQuantity ?? ''}
                  onChange={(e) => setYieldProjection('expectedYieldQuantity', Number.parseFloat(e.target.value) || undefined)}
                  inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth label="Yield Unit" value={form.yieldUnit ?? ''}
                  onChange={(e) => set('yieldUnit', e.target.value as YieldUnit)}>
                  {YIELD_UNITS.map((u) => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <CurrencyInput fullWidth label="Expected Price per Unit"
                  value={form.expectedPricePerUnit ?? null}
                  onChange={(v) => setYieldProjection('expectedPricePerUnit', v ?? undefined)} />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <Button
                    size="small" variant="outlined" startIcon={suggestPriceMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
                    disabled={!form.cropVariety || suggestPriceMutation.isPending}
                    onClick={() => { setPriceSuggestion(null); setPriceSuggestionError(''); suggestPriceMutation.mutate(); }}
                  >
                    Suggest price (AI estimate)
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={estimateYieldMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
                    disabled={estimateYieldMutation.isPending}
                    onClick={() => { setYieldEstimate(null); setYieldEstimateError(''); estimateYieldMutation.mutate(); }}
                  >
                    Estimate yield &amp; revenue (AI estimate)
                  </Button>
                </Stack>
                {priceSuggestionError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setPriceSuggestionError('')}>{priceSuggestionError}</Alert>
                )}
                {priceSuggestion && (
                  <Alert
                    severity={priceSuggestion.error ? 'warning' : 'info'} sx={{ mt: 1.5 }} onClose={() => setPriceSuggestion(null)}
                    action={!priceSuggestion.error && priceSuggestion.pricePerUnit != null && (
                      <Button color="inherit" size="small" onClick={applyPriceSuggestion}>Apply</Button>
                    )}
                  >
                    {priceSuggestion.reasoning}
                  </Alert>
                )}
                {yieldEstimateError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setYieldEstimateError('')}>{yieldEstimateError}</Alert>
                )}
                {yieldEstimate && (
                  <Alert
                    severity={yieldEstimate.error ? 'warning' : 'info'} sx={{ mt: 1.5 }} onClose={() => setYieldEstimate(null)}
                    action={!yieldEstimate.error && (yieldEstimate.yieldQuantity != null || yieldEstimate.pricePerUnit != null) && (
                      <Button color="inherit" size="small" onClick={applyYieldEstimate}>Apply</Button>
                    )}
                  >
                    {yieldEstimate.reasoning}
                    {yieldEstimate.actualCost != null && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
                        Cultivation cost floored to ₦{yieldEstimate.actualCost.toLocaleString('en-NG')} actual recorded task spend.
                      </Typography>
                    )}
                  </Alert>
                )}
                {(yieldEstimate || priceSuggestion) && (
                  <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: chatMessages.length ? 1 : 0 }}>
                      <Chat fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Ask about this estimate — e.g. "is this conservative?" or "what if I add fertilizer?"
                      </Typography>
                    </Stack>
                    {chatMessages.length > 0 && (
                      <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 260, overflowY: 'auto' }}>
                        {chatMessages.map((m, i) => (
                          <Box key={i} sx={{
                            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                            bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                            color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                            px: 1.5, py: 0.75, borderRadius: 2, maxWidth: '85%',
                          }}>
                            <Typography variant="body2">{m.content}</Typography>
                          </Box>
                        ))}
                        {chatMutation.isPending && (
                          <Box sx={{ alignSelf: 'flex-start' }}><CircularProgress size={16} /></Box>
                        )}
                      </Stack>
                    )}
                    {chatError && (
                      <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setChatError('')}>{chatError}</Alert>
                    )}
                    {chatUpdatedEstimate && (
                      <Alert
                        severity="success" sx={{ mb: 1.5 }} onClose={() => setChatUpdatedEstimate(null)}
                        action={
                          <Button color="inherit" size="small" onClick={applyChatUpdatedEstimate}>Apply</Button>
                        }
                      >
                        The AI proposed updated numbers based on that answer — Apply to fill them into the form above.
                      </Alert>
                    )}
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth size="small" placeholder="Ask a follow-up..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                        disabled={chatMutation.isPending}
                      />
                      <IconButton color="primary" onClick={sendChatMessage} disabled={!chatInput.trim() || chatMutation.isPending}>
                        <Send fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth disabled label="Estimated Gross Revenue (₦, auto-computed)"
                  value={form.expectedRevenue ? form.expectedRevenue.toLocaleString('en-NG') : ''}
                  helperText="Quantity × Price per unit" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth disabled label="Estimated Final Cost (₦)"
                  value={form.expectedFinalCost != null ? form.expectedFinalCost.toLocaleString('en-NG') : ''}
                  helperText="Cultivation + harvesting + bagging + transport (AI estimate)" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth disabled label="Estimated Net Revenue (₦)"
                  value={form.expectedNetRevenue != null ? form.expectedNetRevenue.toLocaleString('en-NG') : ''}
                  helperText="Gross revenue − final cost" />
              </Grid>
              {(form.expectedHarvestingCost != null || form.expectedBaggingCost != null || form.expectedTransportationCost != null) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Cost breakdown — Harvesting: {form.expectedHarvestingCost != null ? `₦${form.expectedHarvestingCost.toLocaleString('en-NG')}` : '—'}
                    {' · '}Bagging: {form.expectedBaggingCost != null ? `₦${form.expectedBaggingCost.toLocaleString('en-NG')}` : '—'}
                    {' · '}Transportation: {form.expectedTransportationCost != null ? `₦${form.expectedTransportationCost.toLocaleString('en-NG')}` : '—'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {isLivestock && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Livestock Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Animal Type" value={form.animalType ?? ''}
                  onChange={(e) => set('animalType', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Quantity (head count)" value={form.animalQuantity ?? ''}
                  onChange={(e) => set('animalQuantity', Number.parseInt(e.target.value) || undefined)}
                  inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Expected Revenue (₦)" value={form.expectedRevenue ?? ''}
                  onChange={(e) => set('expectedRevenue', Number.parseFloat(e.target.value) || undefined)}
                  inputProps={{ min: 0 }} />
              </Grid>
            </Grid>
          </Paper>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <AttachmentsPanel entityType="FARM_OPERATION" entityId={opId} />
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate(`/dashboard/farm/${opId}`)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={18} /> : undefined}>
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
